import type { ComponentProps } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import { cn } from '~/lib/cn';

import { Tooltip } from '../../../../components/Tooltip';

interface TruncateWithTooltipProps {
  name: string;
  maxCharacters?: number;
  tooltipPosition?: ComponentProps<typeof Tooltip>['side'];
  className?: string;
}

export function TruncateWithTooltip({
  name,
  maxCharacters = 10,
  tooltipPosition = 'top',
  className,
}: TruncateWithTooltipProps) {
  const spanRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const spanClassName = cn(className, 'max-w-full truncate text-start');

  const contentStyle = { maxWidth: `${maxCharacters}ch` };

  useLayoutEffect(() => {
    const checkOverflow = () => {
      if (spanRef.current) {
        const { scrollWidth, clientWidth } = spanRef.current;

        setIsOverflowing(scrollWidth > clientWidth);
      }
    };

    checkOverflow();
  }, [name, maxCharacters]);

  return (
    <Tooltip
      content={name}
      delayDuration={0}
      enabled={isOverflowing}
      side={tooltipPosition}
      triggerAsChild
      contentClassName="break-all"
    >
      <span className={spanClassName} style={contentStyle} ref={spanRef}>
        {name}
      </span>
    </Tooltip>
  );
}

TruncateWithTooltip.displayName = 'TruncateWithTooltip';
